import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal, Send, Trash2 } from 'lucide-react';
import { useDeleteAnalysis } from '../../../queries/meetingAnalysis';
import { usePublishToClub } from '../../../queries/meetings';
import { useToast } from '../../../contexts/ToastContext';
import { extractErrorMessage } from '../../../api/errors';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';

/**
 * Overflow menu for the least-used meeting actions (Push to Picks App, Delete
 * analysis) — kept out of the primary action row per the meetings-workflow
 * brief. Each item defers to a confirm dialog rendered outside the menu so the
 * menu can close first.
 */
export function MeetingActionsMenu({ meetingId }: { meetingId: string }) {
  const deleteAnalysis = useDeleteAnalysis(meetingId);
  const publish = usePublishToClub();
  const { success, error } = useToast();
  const [confirming, setConfirming] = useState<'delete' | 'publish' | null>(null);

  const doDelete = () =>
    deleteAnalysis.mutate(undefined, {
      onSuccess: () => {
        setConfirming(null);
        success('Deleted all analysis for this meeting.');
      },
      onError: () => error('Failed to delete analysis. Please try again.'),
    });

  const doPublish = () => {
    setConfirming(null);
    publish.mutate(meetingId, {
      onSuccess: (r) => success(`Published ${r.pickCount} picks (${r.lockCount} locks, ${r.valueCount} value).`),
      onError: (err) => error(extractErrorMessage(err, 'Failed to publish.')),
    });
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button variant="outline" size="icon" aria-label="More actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={4}
            className="z-50 min-w-[12rem] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
          >
            <DropdownMenu.Item
              onSelect={() => setConfirming('publish')}
              disabled={publish.isPending}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-accent"
            >
              <Send className="h-3.5 w-3.5" />
              Push to Picks App
            </DropdownMenu.Item>
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            <DropdownMenu.Item
              onSelect={() => setConfirming('delete')}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive outline-none data-[highlighted]:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete analysis
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ConfirmDialog
        open={confirming === 'publish'}
        onOpenChange={(open) => !open && setConfirming(null)}
        title="Publish to the picks app?"
        description="This publishes this meeting's Locks + Value picks to the picks app, where subscribers will see them."
        confirmLabel="Publish"
        busy={publish.isPending}
        onConfirm={doPublish}
      />
      <ConfirmDialog
        open={confirming === 'delete'}
        onOpenChange={(open) => !open && setConfirming(null)}
        title="Delete analysis?"
        description="This permanently removes all generated analysis for every race in this meeting. Runners keep their form and odds, but the tips will need regenerating. This cannot be undone."
        confirmLabel="Delete analysis"
        destructive
        busy={deleteAnalysis.isPending}
        onConfirm={doDelete}
      />
    </>
  );
}
